import { useCallback, useEffect, useMemo, useState } from 'react';

import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import useSideDetail from '#/hooks/useSideDetail';
import { createAsyncFilterLabelCache } from '#/modules/commonComponents/table/asyncFilterLabelCache';
import ListAddButton from '#/modules/commonComponents/table/ListAddButton';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import { hydrateCustomerFilterLabels } from '#/modules/crmMgmt/customer/helper/searchCustomerFilterOptions';
import QuotationSideMsg from '#/modules/crmMgmt/quotation/components/QuotationSideMsg';
import { buildQuotationColumns } from '#/modules/crmMgmt/quotation/data/quotationColumns';
import { getQuotationApiErrorMessage } from '#/modules/crmMgmt/quotation/helper/apiError';
import useQuotationList from '#/modules/crmMgmt/quotation/hooks/useQuotationList';
import { useCan } from '#/modules/infra/auth/useCan';
import PageShell from '#/modules/infra/layout/PageShell';
import SideMsgLayout from '#/modules/infra/layout/SideMsgLayout';

const QuotationListPage = () => {
  const { displayData, setSelectedData } = useSideDetail();
  const canCreate = useCan(PERM.QUOTATION_CREATE);
  const canUpdate = useCan(PERM.QUOTATION_UPDATE);

  const [customerLabelCache] = useState(createAsyncFilterLabelCache);
  const [labelCacheVersion, setLabelCacheVersion] = useState(0);

  const {
    rows,
    pagination,
    isLoading,
    isError,
    error,
    refetch,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    resetListQuery,
  } = useQuotationList();

  useEffect(() => {
    let cancelled = false;

    hydrateCustomerFilterLabels(listQuery.filters?.customerId, customerLabelCache)
      .then((didHydrate) => {
        if (!cancelled && didHydrate) {
          setLabelCacheVersion((version) => version + 1);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [listQuery.filters?.customerId, customerLabelCache]);

  const handleFilterChange = useCallback((key, value) => {
    setFilter(key, value);
  }, [setFilter]);

  const columns = useMemo(
    () => {
      void labelCacheVersion;
      return buildQuotationColumns({
        filters: listQuery.filters || {},
        onFilterChange: handleFilterChange,
        customerLabelCache,
        canShowEdit: (row) => canUpdate && row.status === 'DRAFT',
      });
    },
    [
      listQuery.filters,
      handleFilterChange,
      customerLabelCache,
      labelCacheVersion,
      canUpdate,
    ],
  );

  const filterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(columns, { filters: listQuery.filters }),
    [columns, listQuery.filters],
  );

  const handleRowClick = useCallback((row, isSelected) => {
    setSelectedData(isSelected ? row.original : null);
  }, [setSelectedData]);

  const headerAction = canCreate ? (
    <ListAddButton to={ROUTES.quotationCreate} />
  ) : null;

  return (
    <PageShell variant="listWithSide">
      <div className="layout-shell__body flex min-h-0 flex-col">
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <h1 className="page_title">
            <span className="border-l-5 border-primary-400 pl-2.5">報價單列表</span>
          </h1>
        </div>
        <SideMsgLayout>
          <div className="table-panel min-h-0 xl:h-full">
            <ListTablePanel
              columns={columns}
              rows={rows}
              pagination={pagination}
              isLoading={isLoading}
              isError={isError}
              errorMessage={
                isError
                  ? getQuotationApiErrorMessage(error, '載入列表失敗')
                  : undefined
              }
              onRetry={isError ? () => { refetch(); } : undefined}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              onRowClick={handleRowClick}
              onSearch={setKeyword}
              onReset={resetListQuery}
              initialKeyword={listQuery.keyword}
              searchPlaceholder="搜尋報價單號、客戶編號或名稱"
              listQuery={listQuery}
              filterStatusFields={filterStatusFields}
              onFilterChange={handleFilterChange}
              onKeywordClear={() => setKeyword('')}
              headerAction={headerAction}
            />
          </div>

          <QuotationSideMsg displayData={displayData} />
        </SideMsgLayout>
      </div>
    </PageShell>
  );
};

export default QuotationListPage;
